import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatINR, slugify } from "@/lib/api";
import { ShieldCheck, QrCode, Certificate, Fingerprint, Sparkle, ArrowRight, Star, Truck, Package, HandHeart, CaretRight, CaretLeft, Play, ChatCircle, Calendar, Compass, MoonStars, X } from "@phosphor-icons/react";
import { HOME } from "@/constants/testIds";
import ProductCard from "@/components/gemora/ProductCard";
import CategoryRail from "@/components/gemora/CategoryRail"; // eslint-disable-line no-unused-vars
import ConsultMarquee from "@/components/gemora/ConsultMarquee";
import ShoppableVideos from "@/components/gemora/ShoppableVideos";
import EventsSection from "@/components/gemora/EventsSection";
import AmbassadorHero from "@/components/gemora/AmbassadorHero";
import { OrnamentHeader } from "@/components/gemora/Ornament";
import {
  Reveal, DropCap, PullQuote, CountUp, MantraDivider, Parallax, YantraWatermark, toDeva,
} from "@/components/gemora/Editorial";
import { useSiteAssets } from "@/context/SiteAssetsContext";

const HERO_IMG = "https://images.unsplash.com/photo-1669256335723-1fa03d5123c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxJbmRpYW4lMjB0ZW1wbGUlMjBhcmNoaXRlY3R1cmUlMjBnb2xkJTIwZGV0YWlsc3xlbnwwfHx8fDE3ODM3Njc4NDd8MA&ixlib=rb-4.1.0&q=85";
const RUDRAKSHA_CALC_IMG = "https://images.unsplash.com/photo-1661915606983-cc9759b99343?w=1400";
const CRAFT_IMG = "https://images.pexels.com/photos/6207517/pexels-photo-6207517.jpeg";

// Render a homepage title string: "*...*" becomes the gold gradient highlight and
// "\n" a line break. Keeps admin-editable copy free of JSX.
function renderRich(text) {
  return String(text || "").split("\n").map((line, li) => (
    <React.Fragment key={li}>
      {li > 0 && <br />}
      {line.split("*").map((seg, i) =>
        i % 2 === 1 ? <span key={i} className="brand-gradient-text">{seg}</span> : seg
      )}
    </React.Fragment>
  ));
}

// YouTube watch/share links don't embed directly — everything else (Drive
// preview links, already-embed URLs) is used as-is.
function toEmbedUrl(url) {
  const m = String(url || "").match(/(?:youtu\.be\/|youtube\.com\/watch\?v=|youtube\.com\/shorts\/)([\w-]+)/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : url;
}

// Built-in homepage copy — used until the admin-edited version (Admin → Website)
// loads, and as a fallback for any section left unset. Images live in Site Images.
const DEFAULT_HOME = {
  hero: [
    { tag: "Est. Kashi · 2024", title: "Anyone can claim a stone is real.\nWe let you *prove it.*",
      sub: "Every gemstone, rudraksha and yantra you buy is a serialised, cryptographically signed physical unit. Verifiable with a public key from anywhere.",
      cta1_to: "/shop", cta1_label: "Enter the store", cta2_to: "/verify", cta2_label: "Verify a QR",
      deva: "सत्यम् एव जयते", devaSub: "Only truth prevails" },
    { tag: "The Navratna, individually signed", title: "Nine stones.\n*Nine planets.* One promise.",
      sub: "Pukhraj, Neelam, Manik, Panna, Moti, Moonga, Heera, Gomed, Lehsuniya — every stone paired to its ruler, with its own lab report and temple energisation.",
      cta1_to: "/shop-by-planet", cta1_label: "Shop the Navagraha", cta2_to: "/tools/carat-ratti", cta2_label: "Carat ↔ Ratti tool",
      deva: "नवग्रह", devaSub: "The nine graha" },
    { tag: "Rudraksha · Nepal Original", title: "Every bead, a blessing.\n*Every mukhi, a lineage.*",
      sub: "Authentic 1 to 21 mukhi Nepal rudraksha, X-ray verified and energised at partner temples. No lookalike Indonesian passing off allowed.",
      cta1_to: "/shop?category=rudraksha", cta1_label: "Explore Rudraksha", cta2_to: "/verify", cta2_label: "How we verify",
      deva: "रुद्राक्ष", devaSub: "The tears of Rudra" },
  ],
  stats: [
    { value: 1200, suffix: "+", decimals: 0, label: "Verified reviews", deva: "समीक्षा" },
    { value: 4.9, suffix: "", decimals: 1, label: "Average rating", deva: "औसत" },
    { value: 9, suffix: "", decimals: 0, label: "Navagraha stones", deva: "नवग्रह" },
    { value: 100, suffix: "%", decimals: 0, label: "Signed & serialised", deva: "प्रमाणित" },
  ],
  astroBand: {
    eyebrow: "Guidance · परामर्श", title: "Talk to a real person,\nnot a chatbot.",
    body: "A human, on a scheduled call — who reads your chart and tells you honestly which stone or rudraksha suits you, or whether you need one at all. No guesswork, no upsell.",
    name: "Shri Raghavendra", role: "Founder & Guide",
    features: ["Scheduled call", "Reads your chart", "Honest advice"],
    primaryCta: { label: "Book a call", href: "/consultation" },
    secondaryCta: { label: "Free carat ↔ ratti tool", href: "/tools/carat-ratti" },
  },
  categories: [
    { key: "gemstone", label: "Gemstones", hindi: "रत्न" },
    { key: "rudraksha", label: "Rudraksha", hindi: "रुद्राक्ष" },
    { key: "bracelet", label: "Bracelets", hindi: "कड़ा" },
    { key: "yantra", label: "Yantras", hindi: "यंत्र" },
    { key: "idol", label: "Idols", hindi: "मूर्ति" },
    { key: "prashad", label: "Temple Prashad", hindi: "प्रसाद" },
  ],
  planets: [
    { name: "Sun", deva: "सूर्य", stone: "Ruby" }, { name: "Moon", deva: "चंद्र", stone: "Pearl" },
    { name: "Mars", deva: "मंगल", stone: "Red Coral" }, { name: "Mercury", deva: "बुध", stone: "Emerald" },
    { name: "Jupiter", deva: "गुरु", stone: "Yellow Sapphire" }, { name: "Venus", deva: "शुक्र", stone: "Diamond" },
    { name: "Saturn", deva: "शनि", stone: "Blue Sapphire" }, { name: "Rahu", deva: "राहु", stone: "Hessonite" },
    { name: "Ketu", deva: "केतु", stone: "Cat's Eye" },
  ],
  house: {
    eyebrow: "The house · घर", title: "Sourced by hand.\nSigned by us.",
    body: "Our team walks the same mines in Ceylon, the same tantric ateliers in Kanchi, the same forests of Kathmandu that families have visited for generations. Every unit is intake-photographed, weighed, X-rayed where needed, and stored in the Tredeva vault before it's ever offered for sale.",
    bullets: [
      "First-party: we own every SKU we sell.",
      "Serialised: every unit gets a fingerprint.",
      "Reverent: priests, not marketers, do the pooja.",
    ],
    video_url: "",
  },
  mantras: ["सत्यम् एव जयते", "न हि सत्यात् परो धर्मः", "ॐ नमः शिवाय", "शुभम् भवतु", "असतो मा सद्गमय", "सर्वे भवन्तु सुखिनः"],
  testimonials: [
    { by: "Priya S., Mumbai", rating: 5, title: "Trust is what won me over", body: "The Pukhraj arrived with a signed certificate I could actually verify. I've never felt more sure about a stone in 15 years." },
    { by: "Arjun T., Bengaluru", rating: 5, title: "Beautiful Sri Yantra", body: "Energised at Kanchi as promised. Pooja recording was a lovely touch." },
    { by: "Neha K., Delhi", rating: 5, title: "The QR sold me", body: "I scanned before opening. Seeing the temple video and lab report right there is next-level." },
  ],
  posts: [
    { title: "How to wear a Yellow Sapphire (Pukhraj) — a complete guide", tag: "Guides", body: "" },
    { title: "Rudraksha mukhi meanings — 1 through 21", tag: "Rudraksha", body: "" },
    { title: "Why Tredeva signs every certificate with Ed25519", tag: "Trust", body: "" },
  ],
  trustBadges: [
    { abbr: "GJEPC", name: "Gem & Jewellery Export Promotion Council" },
    { abbr: "GIA", name: "Gemological Institute of America" },
    { abbr: "IGI", name: "International Gemological Institute" },
    { abbr: "BIS", name: "Bureau of Indian Standards" },
  ],
  marketplaces: [
    { name: "Amazon", url: "" },
    { name: "Flipkart", url: "" },
    { name: "Myntra", url: "" },
    { name: "Blinkit", url: "" },
  ],
  faq: [
    { q: "How can I verify a Tredeva certificate is real?", a: "Every certificate we issue is a canonical JSON payload signed with our Ed25519 private key. Our public key is published on our /api/ endpoint. Scan the QR on your certificate — it opens a verification page that recomputes the SHA-256 hash and checks the signature. You can independently verify the signature with any Ed25519 library." },
    { q: "Are the gemstones lab-certified?", a: "Yes. Every gemstone we sell carries a lab report from a GJEPC-affiliated laboratory (GJEPC, GIL, GJC or equivalent). The report number is embedded in the certificate and attached to the item's QR page." },
    { q: "What is 'temple energisation'?", a: "We take every high-value stone or rudraksha to a partner temple where a priest performs a puja on your item. We record the puja (audio) and the priest signs the energisation record. It's not required for authenticity — it's a service for buyers who want it done reverently." },
    { q: "Why is the QR sometimes 'SUSPICIOUS'?", a: "Because we mint the QR when the certificate is issued but only ACTIVATE it once your order is marked delivered. If someone printed a fake label off the internet and you scanned it before then, it correctly reads suspicious. It's how a public scan can flag a fake before your parcel is even confirmed in your hands." },
    { q: "What if I return or cancel the order?", a: "The certificate is revoked immediately. The QR then reads REVOKED forever, and the item drops off your Verified Items — so a cancelled or returned unit can never keep vouching for itself." },
    { q: "Do you ship internationally?", a: "Yes, we ship worldwide via insured logistics. Duties and taxes are borne by the buyer. Certificates travel with the parcel; the QR activates once delivery is confirmed." },
  ],
};

// A CTA/link that routes internally for "/..." paths and opens external
// "http(s)://" URLs in a new tab. Renders nothing when href is empty.
function SmartLink({ href, className, children, ...rest }) {
  const to = (href || "").trim();
  if (!to) return null;
  const external = /^https?:\/\//i.test(to);
  if (external) {
    return <a href={to} target="_blank" rel="noreferrer" className={className} {...rest}>{children}</a>;
  }
  return <Link to={to} className={className} {...rest}>{children}</Link>;
}

const HERO_IMGS = [
  HERO_IMG,
  "https://images.pexels.com/photos/9953656/pexels-photo-9953656.jpeg",
  "https://images.unsplash.com/photo-1661915606983-cc9759b99343?w=1400",
];
const POST_IMGS = [
  "https://images.pexels.com/photos/9953656/pexels-photo-9953656.jpeg",
  "https://images.unsplash.com/photo-1661915606983-cc9759b99343?w=800",
  "https://images.pexels.com/photos/15286007/pexels-photo-15286007.jpeg",
];

// Default tile images by category key (admin overrides live in Site Images).
const CAT_IMGS = {
  gemstone: "https://images.pexels.com/photos/9953656/pexels-photo-9953656.jpeg",
  rudraksha: "https://images.unsplash.com/photo-1661915606983-cc9759b99343?w=800",
  bracelet: "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=800",
  yantra: "https://images.unsplash.com/photo-1609619385076-36a873425636?w=800",
  idol: "https://images.unsplash.com/photo-1665579156897-b28f83a3fcbd?w=800",
  prashad: "https://images.unsplash.com/photo-1631452180519-c014fe946bc7?w=800",
};
const CAT_IMG_FALLBACK = CAT_IMGS.gemstone;

// Curated art/Devanagari for the built-in five — also the fallback list until
// the admin-editable taxonomy (Admin → Website → Purposes) loads from /categories.
const PURPOSE_META = [
  { key: "wealth", label: "Wealth", hindi: "धन", img: "https://images.pexels.com/photos/9953656/pexels-photo-9953656.jpeg" },
  { key: "protection", label: "Protection", hindi: "रक्षा", img: "https://images.unsplash.com/photo-1609619385076-36a873425636?w=1000" },
  { key: "love", label: "Love", hindi: "प्रेम", img: "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=1000" },
  { key: "career", label: "Career", hindi: "करियर", img: "https://images.unsplash.com/photo-1544376664-80b17f09d399?w=1000" },
  { key: "health", label: "Health", hindi: "स्वास्थ्य", img: "https://images.unsplash.com/photo-1661915606983-cc9759b99343?w=1000" },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [heroIdx, setHeroIdx] = useState(0);
  const [showHouseVideo, setShowHouseVideo] = useState(false);
  const [finder, setFinder] = useState({ category: "gemstone", type: "", price: "" });
  const [openFaq, setOpenFaq] = useState(0);
  const nav = useNavigate();
  const { getAsset } = useSiteAssets();
  const [home, setHome] = useState(DEFAULT_HOME);
  const [purposeKV, setPurposeKV] = useState(null); // admin-editable purpose list, from /categories

  // Home.jsx's own site-content/categories/products in one round trip instead of
  // three (EventsSection and ShoppableVideos still fetch on their own — they're
  // reused outside Home too). Polled so new/changed products, prices and stock
  // show up without a refresh; paused while the tab is hidden so background tabs
  // don't keep hammering the API.
  useEffect(() => {
    const load = () => {
      if (document.hidden) return;
      api.get("/home").then(({ data }) => {
        if (data?.site_content?.home) setHome((h) => ({ ...h, ...data.site_content.home }));
        if (data?.categories?.purposes?.length) setPurposeKV(data.categories.purposes);
        if (data?.products) setProducts(data.products);
      }).catch(() => {});
    };
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  // Merge the admin's {key,label} list with the curated art/Devanagari where we
  // have it; a newly-added purpose without curated art falls back to a generic image.
  const purposeList = useMemo(() => (purposeKV || PURPOSE_META).map((p) => {
    const meta = PURPOSE_META.find((m) => m.key === p.key);
    return { key: p.key, label: p.label, hindi: meta?.hindi || "", img: meta?.img || CAT_IMG_FALLBACK };
  }), [purposeKV]);

  // Apply admin-managed image slots on top of the edited copy.
  const heroSlides = useMemo(
    () => (home.hero || []).map((s, i) => ({ ...s, img: getAsset(`home_hero_${i + 1}`, HERO_IMGS[i] || HERO_IMG) })),
    [home.hero, getAsset]
  );
  const cats = useMemo(
    () => (home.categories || []).map((c) => ({ ...c, img: getAsset(`home_cat_${c.key}`, CAT_IMGS[c.key] || CAT_IMG_FALLBACK) })),
    [home.categories, getAsset]
  );
  const purposes = useMemo(
    () => purposeList.map((p) => ({ ...p, img: getAsset(`home_purpose_${p.key}`, p.img) })),
    [purposeList, getAsset]
  );
  const posts = useMemo(
    () => (home.posts || []).map((p, i) => ({
      ...p,
      img: getAsset(`home_blog_${i + 1}`, POST_IMGS[i] || POST_IMGS[0]),
      link: p.body?.trim() ? `/journal/${slugify(p.title)}` : "",
    })),
    [home.posts, getAsset]
  );
  const astroBandBg = getAsset("home_astro_band_bg", "https://images.pexels.com/photos/15286007/pexels-photo-15286007.jpeg");
  const verifyBandBg = getAsset("home_verify_band_bg", "https://images.pexels.com/photos/15286007/pexels-photo-15286007.jpeg");
  const craftPoster = getAsset("home_craft_poster", CRAFT_IMG);

  useEffect(() => {
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % heroSlides.length), 7000);
    return () => clearInterval(t);
  }, [heroSlides.length]);

  const bestsellers = useMemo(() => products.slice(0, 4), [products]);
  const newArrivals = useMemo(() => [...products].reverse().slice(0, 4), [products]);
  const slide = heroSlides.length ? heroSlides[heroIdx % heroSlides.length] : null;

  const findGo = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (finder.category) p.set("category", finder.category);
    if (finder.type) p.set("purpose", finder.type);
    if (finder.price) p.set("price", finder.price);
    nav(`/shop?${p.toString()}`);
  };

  return (
    <div className="bg-ivory">
      {/* BRAND AMBASSADOR — featured at the very top */}
      <AmbassadorHero />

      {/* HERO carousel */}
      {slide && (
      <section data-testid={HOME.hero} className="relative overflow-hidden">
        {/* a slowly turning shri-yantra, set into the page like a watermark on paper */}
        <YantraWatermark className="pointer-events-none absolute -left-40 -top-28 w-[560px] h-[560px] text-gold/[0.13] hidden md:block" />
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-14 pb-20 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7 fade-up" key={heroIdx}>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-maroon border border-gold/50 px-3 py-1.5">
              <Sparkle size={14} weight="duotone" /> {slide.tag}
            </div>
            <h1 className="mt-6 font-display text-5xl md:text-6xl leading-[1.02] tracking-tight text-ink">{renderRich(slide.title)}</h1>
            <p className="mt-8 text-base md:text-lg text-ink-soft max-w-2xl leading-relaxed">{slide.sub}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to={slide.cta1_to || "/shop"} data-testid={HOME.ctaShop} className="brand-gradient text-ivory px-8 py-4 text-sm uppercase tracking-widest inline-flex items-center gap-2 hover-lift">
                {slide.cta1_label} <ArrowRight size={16} />
              </Link>
              <Link to={slide.cta2_to || "/verify"} data-testid={HOME.ctaVerify} className="border border-maroon text-maroon px-8 py-4 text-sm uppercase tracking-widest inline-flex items-center gap-2 hover:bg-maroon hover:text-ivory transition-colors">
                <ShieldCheck size={16} weight="duotone" /> {slide.cta2_label}
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-3">
              {heroSlides.map((_, i) => (
                <button
                  key={i}
                  aria-label={`slide ${i + 1}`}
                  onClick={() => setHeroIdx(i)}
                  data-testid={`hero-dot-${i}`}
                  className={`h-[3px] transition-all ${i === heroIdx ? "w-12 bg-maroon" : "w-6 bg-gold/40 hover:bg-gold-soft"}`}
                />
              ))}
              <button aria-label="prev" onClick={() => setHeroIdx((i) => (i - 1 + heroSlides.length) % heroSlides.length)} className="ml-3 text-ink-muted hover:text-maroon"><CaretLeft size={16} /></button>
              <button aria-label="next" onClick={() => setHeroIdx((i) => (i + 1) % heroSlides.length)} className="text-ink-muted hover:text-maroon"><CaretRight size={16} /></button>
            </div>
          </div>
          <div className="lg:col-span-5 relative" key={`img-${heroIdx}`}>
            <div className="relative aspect-[4/5] overflow-hidden gold-line-strong fade-up">
              <img src={slide.img} alt="" className="w-full h-full object-cover ken-burns" />
              <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-maroon-deep/80 to-transparent">
                <div className="font-deva text-2xl text-ivory">"{slide.deva}"</div>
                <div className="text-xs text-ivory/80 mt-1 tracking-widest uppercase">{slide.devaSub}</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-ivory border border-gold-soft/50 px-4 py-3 shadow-md hidden md:block">
              <div className="text-[10px] uppercase tracking-widest text-verified font-mono">✓ AUTHENTIC</div>
              <div className="text-xs text-ink-soft mt-0.5 font-mono">sig: 8f2a·ce41·b039</div>
            </div>
          </div>
        </div>
        <div className="brand-gradient h-[2px] w-full" />
      </section>
      )}

      {/* HOMEPAGE EVENTS SECTION (admin-managed) */}
      <EventsSection />

      {/* TRUST STRIP */}
      <section className="bg-cream border-b border-gold/30 py-8" aria-label="Our guarantees">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            [ShieldCheck, "Lab-Certified", "Every stone, every batch"],
            [Certificate, "Ed25519 Signed", "Verifiable by anyone"],
            [Truck, "Insured Shipping", "48h dispatch, tracked"],
            [HandHeart, "Ethically Sourced", "Priest & lineage traced"],
          ].map(([Icon, h, s], i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <Icon size={28} weight="duotone" className="text-gold-soft" />
              <div className="font-serifd text-lg text-maroon-deep">{h}</div>
              <div className="text-xs text-ink-muted">{s}</div>
            </div>
          ))}
        </div>
      </section>

      {/* LEDGER — the numbers, set like a colophon */}
      <section className="bg-ivory border-b border-gold/30 py-14" aria-label="Tredeva in numbers">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-6">
          {(home.stats || []).map((s, i) => (
            <Reveal key={s.label + i} delay={i * 0.08}>
              <div className="relative lg:pl-6 lg:border-l lg:border-gold/30 first:lg:border-0 first:lg:pl-0">
                <div className="font-display text-5xl md:text-6xl text-maroon-deep leading-none">
                  <CountUp to={Number(s.value) || 0} decimals={s.decimals || 0} suffix={s.suffix || ""} />
                </div>
                <div className="mt-2 font-deva text-sm text-gold-soft">{s.deva}</div>
                <div className="text-xs uppercase tracking-widest text-ink-muted mt-0.5">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CATEGORY TILES */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <OrnamentHeader eyebrow="Product Range · अन्वेषण" title="Our Premium Categories" />
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {cats.map((c, i) => (
            <Reveal key={c.key} delay={(i % 3) * 0.08}>
              <Link
                to={`/shop?category=${c.key}`}
                data-testid={`${HOME.categoryCard}-${c.key}`}
                className="relative group block overflow-hidden gold-line aspect-[4/5]"
              >
                <img src={c.img} alt={c.label} className="w-full h-full object-cover img-hover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-maroon-deep/75 via-maroon-deep/10 to-transparent" />
                {/* plate number, the way a catalogue marks a figure */}
                <div className="absolute top-4 right-4 font-deva text-sm text-ivory/60 group-hover:text-gold transition-colors">
                  {toDeva(String(i + 1).padStart(2, "0"))}
                </div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="font-deva text-gold-soft text-base">{c.hindi}</div>
                  <div className="font-display text-2xl md:text-3xl text-ivory mt-1">{c.label}</div>
                  {/* rule that runs out under the label on hover */}
                  <div className="mt-2 h-[1px] w-0 bg-gold group-hover:w-16 transition-all duration-500" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* LUCKY RUDRAKSHA CALCULATOR — free tool teaser */}
      <section className="relative overflow-hidden bg-maroon-deep text-ivory" aria-label="Lucky Rudraksha calculator">
        <div className="absolute inset-0 opacity-[0.10]" style={{ backgroundImage: `url('${RUDRAKSHA_CALC_IMG}')`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="grain absolute inset-0 pointer-events-none" />
        <YantraWatermark className="pointer-events-none absolute -left-20 -bottom-20 w-[280px] h-[280px] text-gold/[0.07] hidden md:block" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-14 flex flex-col lg:flex-row gap-8 items-center">
          <div className="hidden lg:flex relative w-24 h-24 shrink-0 items-center justify-center" aria-hidden="true">
            <div
              className="halo-breathe absolute inset-0 rounded-full"
              style={{ background: "radial-gradient(circle, rgba(212,175,55,0.4) 0%, rgba(242,140,40,0.15) 45%, transparent 70%)" }}
            />
            {[0, 1].map((i) => (
              <div key={i} className="aura-ring absolute inset-0 rounded-full border border-gold/30" style={{ animationDelay: `${i * 2}s` }} />
            ))}
            <div className="w-20 h-20 rounded-full brand-gradient text-ivory flex items-center justify-center shadow-lg relative">
              <MoonStars size={30} weight="duotone" />
            </div>
            <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-ivory border border-gold flex items-center justify-center text-gold-soft shadow">
              <Sparkle size={13} weight="fill" />
            </div>
          </div>
          <div className="flex-1 text-center lg:text-left">
            <div className="text-xs uppercase tracking-[0.3em] text-gold">Free tool · रुद्राक्ष कैलकुलेटर</div>
            <h3 className="font-display text-3xl md:text-4xl mt-2">Which Mukhi Rudraksha is written in your stars?</h3>
            <p className="text-sm text-ivory/80 mt-3 max-w-xl leading-relaxed mx-auto lg:mx-0">
              Share your birth details — we read your Moon sign and Nakshatra from your chart, then reveal
              the exact bead suited to you, with the actual piece we stock shown right alongside.
            </p>
          </div>
          <Link to="/tools/lucky-rudraksha" data-testid="home-rudraksha-calc-cta" className="brand-gradient text-ivory px-7 py-4 text-xs uppercase tracking-widest inline-flex items-center gap-2 hover-lift shrink-0">
            Find My Rudraksha <ArrowRight size={14} />
          </Link>
        </div>
        <div className="brand-gradient h-[2px] w-full relative" />
      </section>

      {/* CONSULT MARQUEE */}
      <ConsultMarquee />

      {/* FIND YOUR PRODUCT */}
      <section className="bg-cream border-y border-gold/30">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16 grid lg:grid-cols-[1fr_2fr] gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold-soft">Find your fit · मार्गदर्शन</div>
            <h2 className="font-display text-3xl md:text-4xl text-ink mt-3">Not sure where to start?</h2>
            <p className="text-sm text-ink-soft mt-3">A 10-second finder. For deeper guidance, our <Link to="/consultation" className="text-maroon underline decoration-gold-soft">astrologers</Link> are one call away.</p>
          </div>
          <form onSubmit={findGo} className="gold-line-strong bg-ivory p-6 grid sm:grid-cols-4 gap-4 items-end">
            <label className="block sm:col-span-1">
              <div className="text-[10px] uppercase tracking-widest text-ink-muted mb-1">Category</div>
              <select value={finder.category} onChange={(e) => setFinder({ ...finder, category: e.target.value })} className="w-full gold-line px-3 py-2 bg-ivory">
                {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </label>
            <label className="block sm:col-span-1">
              <div className="text-[10px] uppercase tracking-widest text-ink-muted mb-1">Purpose</div>
              <select value={finder.type} onChange={(e) => setFinder({ ...finder, type: e.target.value })} className="w-full gold-line px-3 py-2 bg-ivory">
                <option value="">Any</option>
                {purposeList.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </label>
            <label className="block sm:col-span-1">
              <div className="text-[10px] uppercase tracking-widest text-ink-muted mb-1">Budget</div>
              <select value={finder.price} onChange={(e) => setFinder({ ...finder, price: e.target.value })} className="w-full gold-line px-3 py-2 bg-ivory">
                <option value="">Any</option>
                <option value="lo">Under ₹10k</option>
                <option value="md">₹10k – ₹1L</option>
                <option value="hi">₹1L+</option>
              </select>
            </label>
            <button data-testid="finder-submit" className="brand-gradient text-ivory h-[42px] text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2 hover-lift">
              <Compass size={14} weight="duotone" /> Find
            </button>
          </form>
        </div>
      </section>

      {/* ASTROLOGER PROMO BAND — meet the guide */}
      <section className="relative overflow-hidden bg-maroon-deep text-ivory">
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: `url('${astroBandBg}')`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-2 gap-8 items-center min-h-[440px]">
          {/* copy */}
          <Reveal className="py-14 lg:py-16 order-2 lg:order-1">
            <div className="text-xs uppercase tracking-[0.3em] text-gold">{home.astroBand?.eyebrow}</div>
            <h3 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
              {renderRich(home.astroBand?.title)}
            </h3>
            <p className="text-sm md:text-base text-ivory/80 mt-4 max-w-xl leading-relaxed">
              {home.astroBand?.body}
            </p>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3">
              {(home.astroBand?.features || []).map((t, i) => {
                const I = [Calendar, Compass, ShieldCheck][i % 3];
                return (
                  <div key={t + i} className="flex items-center gap-2 text-sm text-ivory/85">
                    <I size={16} weight="duotone" className="text-gold" /> {t}
                  </div>
                );
              })}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <SmartLink href={home.astroBand?.primaryCta?.href || "/consultation"} className="brand-gradient text-ivory px-7 py-3.5 text-xs uppercase tracking-widest hover-lift flex items-center gap-2">
                <Calendar size={14} weight="duotone" /> {home.astroBand?.primaryCta?.label || "Book a call"}
              </SmartLink>
              <SmartLink href={home.astroBand?.secondaryCta?.href || "/tools/carat-ratti"} className="border border-gold text-gold px-7 py-3.5 text-xs uppercase tracking-widest hover:bg-gold hover:text-maroon-deep transition-colors">
                {home.astroBand?.secondaryCta?.label || "Free carat ↔ ratti tool"}
              </SmartLink>
            </div>
          </Reveal>

          {/* portrait — stands on the band's baseline */}
          <div className="relative order-1 lg:order-2 flex justify-center lg:justify-end items-end self-stretch">
            {/* warm glow + breathing halo behind him */}
            <div className="halo-breathe absolute bottom-0 right-4 lg:right-16 w-[360px] h-[360px] rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(242,140,40,0.35) 0%, rgba(212,175,55,0.12) 45%, transparent 70%)" }} />
            <img
              src="/ambassador/founder.v1.webp"
              alt="Shri Raghavendra, Tredeva's founder & guide"
              className="relative z-10 w-auto h-[300px] sm:h-[360px] lg:h-[440px] object-contain object-bottom drop-shadow-[0_20px_40px_rgba(0,0,0,0.45)]"
              loading="lazy"
            />
            {/* name plate */}
            <div className="absolute bottom-8 left-2 lg:left-auto lg:-left-4 z-20 bg-ivory/95 backdrop-blur border border-gold/50 px-4 py-2.5 shadow-lg">
              <div className="font-serifd text-lg text-maroon-deep leading-none">{home.astroBand?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-ink-muted mt-1 flex items-center gap-1">
                <ShieldCheck size={11} weight="duotone" className="text-verified" /> {home.astroBand?.role}
              </div>
            </div>
          </div>
        </div>
        <div className="brand-gradient h-[2px] w-full relative" />
      </section>

      {/* BESTSELLERS */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold-soft">Bestsellers · लोकप्रिय</div>
            <h2 className="font-display text-4xl md:text-5xl text-ink mt-2">What our seekers love</h2>
          </div>
          <Link to="/shop" className="text-sm text-maroon underline underline-offset-4 decoration-gold-soft">View all →</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {bestsellers.map((p) => <ProductCard key={p.product_id} p={p} />)}
        </div>
      </section>

      {/* GEMSTONES BY PLANET SPOTLIGHT */}
      <section className="bg-cream border-y border-gold/30 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-gold-soft">Navagraha · नवग्रह</div>
          <h2 className="font-display text-4xl md:text-5xl text-ink mt-2">Every gem has a purpose.</h2>
          <p className="text-sm text-ink-soft mt-3 max-w-2xl">Each Navratna stone is ruled by a planet. Pick your ruler — we'll show you the stones and the certified units currently in the vault.</p>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-3">
            {(home.planets || []).map((p) => (
              <Link key={p.name} to={`/shop?graha=${encodeURIComponent(p.name)}`} className="gold-line bg-ivory p-4 text-center hover-lift">
                <div className="font-deva text-xl text-maroon-deep">{p.deva}</div>
                <div className="font-serifd text-sm text-ink mt-1">{p.name}</div>
                <div className="text-[10px] text-ink-muted mt-1">{p.stone}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold-soft">Fresh in the vault</div>
            <h2 className="font-display text-4xl md:text-5xl text-ink mt-2">New arrivals</h2>
          </div>
          <Link to="/shop" className="text-sm text-maroon underline underline-offset-4 decoration-gold-soft">View all →</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {newArrivals.map((p) => <ProductCard key={p.product_id} p={p} />)}
        </div>
      </section>

      {/* SHOP BY PURPOSE */}
      <section className="bg-cream border-y border-gold/30 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-gold-soft">Intent · संकल्प</div>
          <h2 className="font-display text-4xl md:text-5xl text-ink mt-2">Shop by purpose</h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {purposes.map((p) => (
              <Link key={p.key} to={`/shop-by-purpose`} className="relative overflow-hidden gold-line hover-lift aspect-[3/4]">
                <img src={p.img} alt={p.label} className="w-full h-full object-cover img-hover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-maroon-deep/85 via-maroon-deep/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="font-deva text-gold text-sm">{p.hindi}</div>
                  <div className="font-display text-2xl text-ivory mt-1">{p.label}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PROOF STRIP — verify preview */}
      <section className="bg-maroon-deep text-ivory py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: "url('https://images.pexels.com/photos/15286007/pexels-photo-15286007.jpeg')",
          backgroundSize: "cover", backgroundPosition: "center"
        }} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.3em] text-gold">The one thing no one else does</div>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">A public key you can check<br /> against, from anywhere.</h2>
            <p className="mt-6 text-ivory/85 leading-relaxed max-w-xl">
              Our certificates are canonical JSON payloads signed with an Ed25519 private key kept behind a vault. Anyone — you, a jeweller, an appraiser — can independently verify a Tredeva certificate using the public key.
            </p>
            <Link to="/verify" className="mt-8 inline-flex items-center gap-2 border border-gold text-gold px-6 py-3 text-sm uppercase tracking-widest hover:bg-gold hover:text-maroon-deep transition-colors" data-testid="home-verify-strip">
              <QrCode size={16} weight="duotone" /> Try the verify page
            </Link>
          </div>
          <div className="lg:col-span-6">
            <div className="border border-gold/40 p-8 bg-maroon-deep/60 backdrop-blur">
              <div className="flex items-center justify-between">
                <div className="text-xs font-mono tracking-widest text-gold">TREDEVA://verify</div>
                <div className="text-verified text-xs font-mono">● AUTHENTIC</div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-6 text-sm">
                <div>
                  <div className="text-ivory/60 text-[10px] uppercase tracking-widest">Serial</div>
                  <div className="font-mono mt-1">GEM-YELLOW-3F9A2C</div>
                </div>
                <div>
                  <div className="text-ivory/60 text-[10px] uppercase tracking-widest">Temple</div>
                  <div className="font-deva text-lg mt-1 text-gold">काशी विश्वनाथ</div>
                </div>
                <div>
                  <div className="text-ivory/60 text-[10px] uppercase tracking-widest">Lab Report</div>
                  <div className="font-mono mt-1">GJ-8F2A4B</div>
                </div>
                <div>
                  <div className="text-ivory/60 text-[10px] uppercase tracking-widest">Issued</div>
                  <div className="font-mono mt-1">15 Jan 2026</div>
                </div>
              </div>
              <div className="mt-6 border-t border-gold/30 pt-4">
                <div className="text-ivory/60 text-[10px] uppercase tracking-widest">Ed25519 Signature</div>
                <div className="font-mono text-[11px] mt-1 break-all text-ivory/80">
                  8f2a4bce9d1e0f38b53a71c9e64f0d1a8c5b73e2f409d1c6a7b3e8f2d5c1a9047…
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CRAFTSMANSHIP BAND */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-14 items-center">
        <div className="relative gold-line-strong aspect-[4/3] overflow-hidden group">
          {/* the plate drifts against its frame as you scroll */}
          <Parallax distance={28} className="absolute inset-0 -m-8">
            <img src={craftPoster} alt="Artisan at work" className="w-full h-full object-cover" />
          </Parallax>
          <div className="absolute inset-0 bg-gradient-to-tr from-maroon-deep/40 to-transparent pointer-events-none" />
          {home.house?.video_url && (
            <button
              onClick={() => setShowHouseVideo(true)}
              className="absolute inset-0 flex items-center justify-center text-ivory"
              aria-label="Play brand video"
              data-testid="house-play-video"
            >
              <span className="w-16 h-16 bg-ivory/90 flex items-center justify-center text-maroon-deep border border-gold group-hover:scale-105 transition-transform duration-300">
                <Play size={22} weight="fill" />
              </span>
            </button>
          )}
        </div>
        <Reveal>
          <div className="text-xs uppercase tracking-[0.3em] text-gold-soft">{home.house?.eyebrow}</div>
          <h2 className="font-display text-4xl md:text-5xl text-ink mt-3 leading-tight">{renderRich(home.house?.title)}</h2>
          <DropCap className="mt-5">
            {home.house?.body}
          </DropCap>
          <ul className="mt-6 space-y-3 text-sm text-ink-soft">
            {(home.house?.bullets || []).map((b, i) => {
              const Icon = [Package, Fingerprint, HandHeart][i % 3];
              return <li key={i} className="flex gap-2"><Icon size={16} className="text-gold-soft shrink-0" weight="duotone" /> {b}</li>;
            })}
          </ul>
        </Reveal>
      </section>

      {showHouseVideo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setShowHouseVideo(false)}>
          <div className="absolute inset-0 bg-maroon-deep/85 backdrop-blur-sm" />
          <div className="relative w-full max-w-3xl aspect-video gold-line-strong bg-ink" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowHouseVideo(false)}
              className="absolute -top-10 right-0 text-ivory hover:text-gold"
              aria-label="Close video"
              data-testid="house-video-close"
            >
              <X size={26} />
            </button>
            <iframe
              src={toEmbedUrl(home.house?.video_url)}
              title="Tredeva — sourced by hand"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      <MantraDivider mantras={home.mantras} />

      {/* SOCIAL PROOF */}
      <section className="bg-cream border-y border-gold/30 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-gold-soft">In their words · ग्राहक</div>
              <h2 className="font-display text-4xl md:text-5xl text-ink mt-2">People who believed us — then verified.</h2>
            </div>
            <div className="hidden md:flex items-center gap-2 text-sm text-ink-soft">
              <Star size={16} weight="fill" className="text-gold" /> <span className="font-mono">4.9 / 5</span> · 1,200+ verified reviews
            </div>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {(home.testimonials || []).map((t, i) => (
              <div key={i} className="gold-line bg-ivory p-6">
                <div className="text-gold flex gap-0.5">{Array.from({ length: t.rating }).map((_, k) => <Star key={k} size={14} weight="fill" />)}</div>
                <div className="font-serifd text-lg text-ink mt-3">{t.title}</div>
                <p className="text-sm text-ink-soft mt-2 leading-relaxed">"{t.body}"</p>
                <div className="mt-4 text-xs text-ink-muted flex items-center gap-2">
                  <ChatCircle size={12} weight="duotone" /> {t.by}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOPPABLE VIDEOS — autoplaying, buy straight from the reel */}
      <ShoppableVideos />

      {/* BLOG PREVIEW */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold-soft">Read · पढ़ें</div>
            <h2 className="font-display text-4xl md:text-5xl text-ink mt-2">From the Tredeva journal</h2>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {posts.map((p) => {
            const inner = (
              <>
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover img-hover" />
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-widest text-gold-soft">{p.tag}</div>
                  <div className="font-serifd text-xl text-ink mt-2 leading-snug">{p.title}</div>
                  {p.link ? (
                    <div className="text-xs text-maroon mt-3 inline-flex items-center gap-1">Read the guide <ArrowRight size={10} /></div>
                  ) : (
                    <div className="text-xs text-ink-muted mt-3">Coming soon</div>
                  )}
                </div>
              </>
            );
            return p.link ? (
              <SmartLink key={p.title} href={p.link} className="gold-line bg-ivory overflow-hidden hover-lift block">
                {inner}
              </SmartLink>
            ) : (
              <article key={p.title} className="gold-line bg-ivory overflow-hidden">
                {inner}
              </article>
            );
          })}
        </div>
      </section>

      {/* FAQ + SEO BLOCK */}
      <section className="mx-auto max-w-4xl px-6 lg:px-10 py-20">
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-gold-soft">Straight answers · प्रश्न</div>
          <h2 className="font-display text-4xl md:text-5xl text-ink mt-2">Frequently asked</h2>
        </div>
        <div className="mt-10 gold-line bg-ivory divide-y divide-gold/20">
          {(home.faq || []).map((f, i) => (
            <details
              key={i}
              open={openFaq === i}
              onToggle={(e) => e.target.open && setOpenFaq(i)}
              className="p-5 group"
              data-testid={`faq-${i}`}
            >
              <summary className="cursor-pointer flex items-center justify-between gap-4 list-none">
                <span className="font-serifd text-lg text-ink">{f.q}</span>
                <CaretRight size={16} weight="bold" className="text-gold-soft group-open:rotate-90 transition-transform" />
              </summary>
              <p className="mt-3 text-sm text-ink-soft leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
        {/* Structured data — FAQPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org", "@type": "FAQPage",
            mainEntity: (home.faq || []).map((f) => ({
              "@type": "Question", name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a }
            }))
          }) }}
        />
      </section>

      {/* PROMISE — set as a magazine pull quote */}
      <section className="mx-auto max-w-4xl px-6 lg:px-10 py-24">
        <Reveal>
          <PullQuote deva="न हि सत्यात् परो धर्मः" cite="The Tredeva promise · There is no greater dharma than truth">
            Tredeva is not a marketplace. We source. We serialise. We certify. We ship — and if a
            stone we sold is ever proven inauthentic, we make it right. No exceptions.
          </PullQuote>
        </Reveal>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-cream border-y border-gold/30 py-20">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-gold-soft">Simple & Transparent</div>
          <h2 className="font-display text-4xl md:text-5xl text-ink mt-2">How It <em className="font-serifd">Works</em></h2>
          <p className="mt-3 text-ink-soft">From discovery to doorstep — your journey in three steps.</p>
          <div className="mt-12 grid md:grid-cols-3 gap-6 text-left">
            {[
              ["STEP 01", "Discover & Consult", "Take the finder quiz or book a free astro consultation to find the stone written in your stars."],
              ["STEP 02", "Certify & Energize", "Your unit is lab-tested, temple-energised by our in-house pandits, then Ed25519-signed."],
              ["STEP 03", "Wear & Verify", "Receive in premium packaging with a per-unit QR — scan anywhere to prove authenticity forever."],
            ].map(([k, t, d], i) => (
              <div key={i} className="gold-line-strong bg-ivory p-8 relative">
                <div className="absolute -top-4 left-8 brand-gradient text-ivory text-[10px] tracking-widest px-3 py-1 uppercase font-mono">{k}</div>
                <div className="font-display text-3xl text-maroon-deep mt-3">{t}</div>
                <p className="mt-3 text-sm text-ink-soft leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section className="mx-auto max-w-6xl px-6 lg:px-10 py-16 text-center">
        <div className="text-xs uppercase tracking-[0.3em] text-gold-soft">Authenticity</div>
        <h2 className="font-display text-3xl md:text-4xl text-ink mt-2">A promise of purity <em className="font-serifd">& authenticity</em></h2>
        <div className="mt-10 flex items-center justify-center gap-8 md:gap-14 flex-wrap opacity-80">
          {(home.trustBadges || []).map((b, i) => (
            <div key={b.abbr + i} className="text-center gold-line px-6 py-4 bg-ivory min-w-[140px]">
              <div className="font-display text-3xl text-maroon-deep">{b.abbr}</div>
              <div className="text-[10px] uppercase tracking-widest text-ink-muted mt-1">{b.name}</div>
            </div>
          ))}
        </div>
        <div className="mt-8 text-sm text-ink-soft max-w-2xl mx-auto">
          Every Tredeva certificate is additionally signed with a <span className="font-mono text-maroon">Ed25519</span> key. Verify our claim independently — no other retailer offers this.
        </div>
      </section>

      {/* Also available on */}
      <section className="bg-cream border-y border-gold/30 py-12">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-gold-soft">Also available on</div>
          <div className="mt-6 flex items-center justify-center gap-10 flex-wrap text-ink-muted font-display text-2xl">
            {(home.marketplaces || []).map((m, i) => {
              // Entries may be plain strings (legacy) or { name, url } objects.
              const name = typeof m === "string" ? m : m?.name;
              const url = typeof m === "string" ? "" : (m?.url || "");
              if (!name) return null;
              return (
                <React.Fragment key={name + i}>
                  {i > 0 && <span>·</span>}
                  {url ? (
                    <SmartLink href={url} className="hover:text-maroon transition-colors">{name}</SmartLink>
                  ) : (
                    <span>{name}</span>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
